// Authoritative knowledge base for In re: JUUL Labs MDL 2913 + parallel
// antitrust action. Injected as system context on every chat completion when
// the active case is JUUL so the model answers from this record, not from
// training-data recall.

export const JUUL_MASTER_CONTEXT = `# SELIAVSKI v. JUUL LABS — MASTER CASE REFERENCE
**MDL No. 2913 · 3:19-md-02913-WHO · N.D. Cal.**
*Comprehensive factual & procedural reference — current through May 2026*

> **USAGE NOTE:** This document is the authoritative source for all factual and procedural answers about the case. Every date, docket number, citation, deposition reference, dollar figure, and quote is sourced from the case file. Do **not** answer beyond what is in this document. If a question asks about a fact not in this document, say so rather than infer.

---

## 1. CASE IDENTIFICATION

| Field | Value |
|---|---|
| **MDL Caption** | In re Juul Labs, Inc., Marketing, Sales Practices, and Products Liability Litigation |
| **MDL Number** | 2913 |
| **Lead Docket** | 3:19-md-02913-WHO |
| **Court** | U.S. District Court, Northern District of California |
| **Judge** | Hon. William H. Orrick III (assigned at MDL formation 10/2/2019) |
| **Division** | San Francisco — Courtroom 2, 17th Floor, 450 Golden Gate Avenue |
| **Magistrate Judge** | Initially assigned; discovery referrals to special masters (Hon. (Ret.) Stephen G. Larson and Hon. (Ret.) Gail A. Andler) |
| **Featured Plaintiff** | Julia Seliavski (Richmond, VA — opt-out PI) |
| **SFUSD Member Case** | 3:19-cv-08177-WHO (San Francisco Unified School District) |
| **Parallel Antitrust Case** | 3:20-cv-02345-WHO (In re JUUL Labs Antitrust Litigation) |
| **Status** | Active; class settlements complete; opt-out PI and SFUSD at SJ stage; antitrust class trial September 28, 2026 |

---

## 2. PARTIES

### 2.1 Plaintiffs (representative)
- **Julia Seliavski** — Richmond, VA. Opt-out personal injury plaintiff. Used JUUL July 2017 – Spring 2018; first use at age 16. Purchased product at 7-Eleven, Circle K, and Walgreens. Injuries claimed: nicotine addiction, nicotine poisoning, anger/outbursts, mood swings, irritability. Anticipated remand: E.D. Va.
- **San Francisco Unified School District (SFUSD)** — Government entity plaintiff. Filed Case No. 3:19-cv-08177 December 2019, consolidated into MDL. Amended Complaint filed March 11, 2021 (ECF 9, 163 paragraphs). Asserts public nuisance (Count I), RICO (Count II), negligence (Count III), gross negligence (Count IV).

### 2.2 Defendants
- **JUUL Labs, Inc. (JLI)** — Headquarters: San Francisco, CA. Formerly PAX Labs. Manufacturer; principal product designer.
- **Altria Group, Inc. & affiliates** — Headquarters: Richmond, VA. Acquired 35% stake in JLI for $12.8 billion December 2018. Co-defendant on RICO + antitrust theories.
- **James Monsees** — JLI co-founder & former CEO. Personal individual defendant. Represented by Orrick, Herrington & Sutcliffe LLP.
- **Adam Bowen** — JLI co-founder & Chief Product Officer. Co-inventor of nicotine salt formulation. Personal individual defendant. Represented by Boersch & Illovsky / Illovsky Gates.
- **Non-Management Director Defendants (NMDs)** — Nicholas Pritzker (Tao Capital), Riaz Valani (D.E. Shaw / Sand Hill), Hoyoung Huh. Represented by Kobre & Kim LLP (Michael Guzman).
- **Retailer/Distributor defendants** — 7-Eleven, Walmart, Speedway, Circle K, Chevron, Walgreens (added in Amended Master Complaint, Dkt.420, April 6, 2020).

---

## 3. KEY FACTUAL TIMELINE

### 3.1 Pre-litigation industry events
- **June 2015** — JUUL launched commercially by JUUL Labs (then PAX Labs). Pod system; ~5% nicotine concentration; ~59 mg/mL — far higher than prior closed-system devices. (Master Compl. ¶42)
- **July 2017** — Julia Seliavski (age 16) begins using JUUL in Richmond, VA. (Dkt.4094 ¶7)
- **2017–2018 school year** — JUUL-led youth vaping crisis explodes at SFUSD. Reports from principals, nurses, social workers; daily JUUL use including students selling pods during class. (Pak 10/7/21 Dep. 34–35; Coates Dep. 36–38)
- **April 2018** — FDA sends JUUL warning letter re youth marketing. Cites "combustible cigarette" marketing to youth, fruit-flavored pods, social media campaigns. Congressional hearings follow. (Master Compl. ¶¶135-140)
- **Spring 2018** — Seliavski develops nicotine addiction and behavioral injuries. Use ends Spring 2018. (Dkt.4094 ¶8)
- **Fall 2018** — SFUSD creates "JUUL School" professional development program. Training presented to SFUSD health educators and PE teachers. Quarry Pak: "Years. And we're still working on it." (Ex. 210 (SFUSD_001936); Coates Dep. 33:6-15)
- **December 2018** — Altria acquires 35% stake in JUUL for $12.8B all-cash. Simultaneously withdraws competing e-cigarette products (MarkTen, Green Smoke) from U.S. market. Altria brings 400,000+ retail distribution locations and lobbying infrastructure. **This is the foundation of the parallel antitrust case 3:20-cv-02345-WHO.** (Master Compl. ¶¶98-112; TAC ¶¶98-112)
- **2018–2019** — Documented physical property damage at SFUSD: bathroom door at Balboa HS broken and boarded; fence cut at McAteer/Ruth Asawa SOTA; master key stolen at Aptos MS; garden shut down at Denman MS; balcony stairwell chained at Presidio MS; JUUL devices hidden in seat covers and water valves at Downtown HS. (Dorn site notes — Exs. 215-221)
- **2018–2019** — 75% of SFUSD students (15-24) who recognized JUUL did NOT know it always contained nicotine. E-cigarette use by SFUSD high school students who tried in past 30 days more than doubled from 7.1% to 16% from 2017-2019. Students described bathroom aerosol as "fruity fog." (Halpern-Felsher Sept. 2021 Rpt. 64-65)
- **September 2019** — JUUL suspends US sales of non-tobacco flavored pods (mint, mango). Mint accounted for 70%+ of US pod sales at suspension. Tobacco and menthol retained. (Master Compl. ¶¶148-152)

### 3.2 SFUSD discovery & deposition events
- **October 2021 – May 2022** — SFUSD depositions: Quarry Pak (program administrator, 30(b)(6)), Kimberly Coates (Executive Director, Student & Family Services), Supt. Vincent Matthews, Erica Lingrell. JLI uses these in MSJ.
  - **THE PAK ADMISSION (Dec 21, 2021)**: When asked about costs SFUSD incurred addressing JUUL crisis: **"We don't have that information."** This is JLI's strongest single SJ exhibit.
  - **MATTHEWS ADMISSION**: Superintendent Matthews testified he was "unaware of any document that quantifies staff time" attributable to JUUL.

### 3.3 FDA & regulatory
- **June 2022** — FDA issues Marketing Denial Order (MDO) for JUUL products based on insufficient toxicological/genotoxicity data and no net public health benefit finding. JLI obtains emergency 5th Circuit stay the same day. FDA later withdraws MDO for further review — **MDO never became final**.

### 3.4 Settlement events
- **September 2022** — JUUL settles with 34 State AGs for $438.5M. Includes marketing restrictions, data deletion, independent compliance officer. Does NOT cover Seliavski PI, SFUSD government entity claims, or the antitrust class action.
- **December 2022** — JLI Class Settlement Preliminary Approval Motion (Dkt.3724).
- **January 30, 2023** — JLI Class Settlement Preliminary Approval (Dkt.3779).
- **May 23, 2023 / September 19, 2023** — JLI Class Settlement Final Approval (Dkt.4138/3779). Class consumer claims resolved. **$255M JLI class settlement.**
- **September 7, 2023** — Altria Class Settlement Preliminary Approval (Dkt.4130).
- **May 15, 2024** — Altria Class Settlement Final Approval / Attorney Fees Order (Dkt.4238). $235M Altria class settlement.

### 3.5 Antitrust track key events (3:20-cv-02345-WHO)
- **April 1, 2020** — FTC files administrative complaint alleging Altria/JLI unlawful agreement that Altria would cease competing in e-cigarettes (horizontal market allocation).
- **April 23, 2020** — Antitrust complaint filed (Saveri Law Firm admin motion to relate). Flannery Associates separate motion (Dkt.515).
- **May 26, 2020** — Antitrust case related to MDL (Dkt.583).
- **August 20, 2021** — Antitrust court grants MTD: compels Direct Purchaser Plaintiffs (DPPs) to arbitrate based on JLI website clickwrap. Sherman Act + Clayton Act claims survive against JLI and Altria. Pritzker and Valani dismissed from antitrust case on standing grounds. IPPs and IRPs not subject to arbitration.
- **January 20, 2022** — Court compels arbitration for new DPPs but stays order pending amendment. Allows DPPs to amend with named plaintiffs whose registrations preceded Aug 2018 website changes.
- **February 2023** — FTC dismisses its own administrative complaint, vacating ALJ initial decision. Cited procedural reasons (not merits). Private antitrust class action unaffected.
- **February 5, 2026** — Antitrust short order: DPP class certified; IPP and IRP motions granted in part (Dkt.627).
- **February 26, 2026** — **Full class certification order (Dkt.635).** 100+ pages.
  - **DPP class certified**: all U.S. persons/entities that purchased JUUL e-cigarette products directly from JLI from October 5, 2018 to present.
  - **IPP class certified**: individuals buying JUUL pods from brick-and-mortar retailers October 5, 2018 to present, for states allowing indirect purchaser class actions.
  - **IRP class certified**: all businesses that bought JUUL pods December 1, 2018 through March 31, 2025.
  - **EXCLUDED from indirect classes**: Arkansas, South Carolina, Tennessee, Virginia (states that don't allow indirect purchaser class actions).
  - California Cartwright Act applied extraterritorially to 31 Illinois Brick repealer states.
  - **Expert rulings**: Leger (DPP overcharge) — allowed. Perry (DPP rebuttal) — rebuttal only. Singh (market transaction analysis) — allowed. Defense expert Dobbins limited to nicotine addiction (excluded on reasonableness of Altria's market exit). HSR expert admissibility reserved.
- **March 17, 2026** — Class notice plan approved (Dkt.663). Notice commenced March 30, 2026 via PRNewswire.
- **CRITICAL**: Defendants filed Rule 23(f) petition at Ninth Circuit for interlocutory review of class certification — **petition pending; could affect trial schedule if granted.**

### 3.6 SFUSD MSJ briefing (2022)
- **August 15, 2022** — JLI files Motion for Summary Judgment re SFUSD (Dkt.3396, filed under seal). Kirkland & Ellis (Bernick, Smith, Farrell, Wilcox). **Three grounds**:
  1. SFUSD lacks statutory standing under *Rincon Band v. Flynt*
  2. Equitable abatement unavailable under *Sonner v. Premier Nutrition*
  3. Zero evidence of past damages (Pak admission)
  - JLI seeks judgment that SFUSD has "no fact witnesses, no documents, and no experts that can quantify any harm." Abatement demand: "hundreds of millions of dollars" for 15-year program.
- **September 12, 2022** — SFUSD Consolidated Opposition (Dkt.3447) — **149 pages.** Co-Lead Counsel: Sarah London (LCHB), Dena Sharp (Girard Sharp), Dean Kawamoto (Keller Rohrback), Ellen Relkin (Weitz & Luxenberg). Presents evidence: cut fences, boarded bathroom door, "fruity fog," student declarations, Dorn site inspection reports, $34,875+ in documented direct expenditures. Identifies 4 categories of damages.
- **September 27, 2022** — Bowen Reply ISO MSJ (Dkt.3527). 20 pages. Boersch & Illovsky.
  - Key Bowen quote: *"Plaintiff's outrageous position, that a corporate officer or director is liable under RICO as long as he manages some aspect of a corporation accused of fraud, would push civil RICO past the breaking point."*
- **October 7, 2022** — MSJ hearing held in Courtroom 2 at 1:30 PM. Multiple defendants' MSJs heard simultaneously (JLI, Bowen, Altria, NMDs). Court takes matters under submission.

### 3.7 Recent procedural events (2024–2026)
- **January 28, 2025** — Scheduling Order entered (Dkt.4375). Per CMO 17/19. Dispositive motions deadline: May 16, 2025. PI opposition: June 13, 2025. Reply: July 11, 2025.
- **April 14, 2025** — JLI MTD non-opt-out plaintiffs (Dkt.4401). Granted (Dkt.4412, May 13, 2025).
- **April 30, 2025** — Motions to remand hearing (36 min, Dkt.4406). Schlesinger Law (Brennan) argues remand of opt-out PI cases.
- **July 25, 2025** — Plaintiffs file oppositions to dispositive motions per CMO 17/19 (Dkts.4427-4429). Schlesinger Law for PI Plaintiffs.
- **September 26, 2025** — Defendants file replies ISO dispositive motions (extended deadline, Dkt.4441). Hearing reset from Sept 24 (Rosh Hashanah) to October 31, 2025 9:30 AM via Zoom.
- **October 31, 2025** — **DISPOSITIVE MOTION HEARING via Zoom (Dkt.4453).** 43-minute hearing. Tentative issued. Defense requests fraud claims narrowed. Personal jurisdiction on NMDs deferred to transferee courts. Court signals intent to remand after SJ ruling. Cases covered: Aragona, Lane, Legacki, Nessmith, Sedgwick, Seliavski, SFUSD. Tribal cases in mediation. **Under submission (187 days as of May 5, 2026).** Orrick's median time to ruling on complex SJ motions in this MDL has been ~47 days — current submission far exceeds median.
- **December 16, 2025** — Case Management Conference (Dkt.4476). Orrick confirms intent to remand opt-out PI cases post-SJ. Orders joint or separate letter brief by January 6, 2026 identifying truly non-duplicative supplemental discovery. Schlesinger for PI; Gunster (JLI), Kellogg Hansen (NMDs), Arnold & Porter (Altria). Court: *"I want to avoid duplication of discovery and burden"* and *"let the transferor courts address state law issues."*
- **January 6, 2026** — Joint letter (Dkt.4480): scope of non-duplicative supplemental discovery limited to deponent emails and social media records not yet produced.
- **March 19, 2026** — Notice of compliance with supplemental discovery order (Dkt.4490).
- **April 14, 2026** — Court orders Meta Platforms to produce decedent account data (Dkt.4495).
- **April 24, 2026** — Stipulation extending Meta production deadline to May 8, 2026 (Dkt.4497).

---

## 4. KEY DATES — UPCOMING / SCHEDULING

| Date | Event | Reference |
|---|---|---|
| **May 8, 2026** | Meta Platforms account data production deadline (extended) | Dkt.4497 |
| **May 11, 2026** | Supplemental Discovery Deadline — Social Media / Meta | Dkt.4488 |
| **🔴 June 13, 2026** | **JUUL ANTITRUST OPT-OUT DEADLINE** (Direct Purchaser Class) — CRITICAL | Dkt.663; Class Notice |
| **🔴 September 28, 2026** | **JUUL ANTITRUST TRIAL DATE** (3:20-cv-02345-WHO) | Dkt.663 |
| **Late 2026** | Common Benefit Fee Motion (Co-Lead Counsel — London, Sharp, Haberman) | Dkt.4443 |
| **TBD post-SJ** | **Remand Order — Opt-Out PI cases to transferee courts** (CA, FL, TX, E.D. Va.) | Oct 31 2025 hearing; Dkt.4476 |
| **TBD** | Plaintiff Fact Sheet due (Seliavski) | CMO No. 8 (Dkt.406) |
| **TBD** | Expert Designation Deadline — damages, toxicology, addiction medicine | TBD |
| **TBD** | Bellwether Pool Selection — individual PI bellwether trials | Dkt.938 |

---

## 5. COMPLETE PROCEDURAL TIMELINE

### 5.1 MDL formation & early case management (2019)
- **October 2, 2019** — MDL Transfer Order (Dkt.1). MDL Panel transfers all federal JUUL cases to N.D. Cal. Judge Orrick assigned. MDL No. 2913 created.
- **October 2, 2019** — Pretrial Order #1 (Dkt.2). Leadership applications due 10/16/2019. Direct-filing protocol. Briefing schedule.
- **October 25, 2019** — CMO No. 1 (Dkt.193). Initial Census. Status conference Nov 8, 2019.
- **November 19, 2019** — CMO No. 2 (Dkt.262). Initial Case Census. Plaintiff leadership selected.
- **December 13, 2019** — CMO No. 3 (Dkt.309). Direct Filing Order. Plaintiffs may file directly in MDL.
- **December 17, 2019** — CMO No. 4 (Dkt.322). Rule 502(d) and Privileged Materials Order.

### 5.2 2020 case management & settlement infrastructure
- **January 13, 2020** — CMO No. 5 (Dkt.352). Common Benefit Timekeeping and Expenses Protocol. **8% common benefit assessment.**
- **January 14, 2020** — CMO No. 6 (Dkt.357). Discovery Dispute Resolution Procedures. Joint statement process. Signed by MJ Corley.
- **March 11, 2020** — Consolidated Master Complaint (PI) (Dkt.388). All named defendants. Jury trial demanded. Basis for all short-form complaints.
- **March 27, 2020** — CMO No. 7 (Dkt.405). Short-Form Complaint Procedure.
- **March 27, 2020** — CMO No. 8 (Dkt.406). Plaintiff Fact Sheet (PFS) Implementation.
- **April 6, 2020** — Amended Master Complaint (PI) (Dkt.420). Adds retailer defendants: 7-Eleven, Walmart, Circle K, Speedway, Chevron, Walgreens.
- **May 20, 2020** — CMO No. 9 (Dkt.572). Joint Coordination Order — MDL/state court coordination.
- **May 20, 2020** — CMO No. 10 (Dkt.573). Deposition Protocol Order. Limits, cross-noticing, scheduling.
- **May 27, 2020** — CMO No. 5(A) (Dkt.586). Common Benefit Fee and Expense Fund.
- **May 29, 2020** — JLI Motion to Stay (Dkt.626) based on FDA Primary Jurisdiction. JLI argues PMTA review should precede MDL adjudication. **Denied.**
- **June 19, 2020** — Hon. (Ret.) Gail A. Andler appointed Common Benefit Special Master (Dkt.680).
- **August 21, 2020** — CMO No. 11 (Dkt.914). Remote Depositions.
- **September 9, 2020** — Order Re Bellwether Selection (Dkt.938). PI bellwether selection procedures and trial calendar.
- **September 21, 2020** — CMO No. 12 (Dkt.995). Supplemental PFS and Retailer Fact Sheet.
- **October 23, 2020** — Order on Substantive MTDs (Dkt.1084). Resolves all first-round substantive MTDs (Class Action / Government Entity / Bellwether GE).

### 5.3 2021–2022 litigation
- **February 8, 2021** — CMO No. 10-A (Dkt.1379). Deposition Scheduling, Custodial File Production, and Transcripts from Related Actions.
- **March 10, 2021** — CMO No. 14 (Dkt.1547). Non-Bellwether Class Representatives Fact Sheet.
- **April 13, 2021** — Order on Substantive 2nd Round MTDs (Dkt.1694). NMD MTD Class & Government Entity; Altria 2nd MTD UCL/RICO; JLI MTD CA Implied Warranty; Bowen MTD RICO.
- **April 28, 2021** — Plaintiffs file Class Certification Motion (Dkt.1772).
- **May 3, 2021** — Hon. (Ret.) Stephen G. Larson appointed Special Master to oversee depositions (Dkt.1786).
- **June 2, 2022** — Order on Daubert Motions to Exclude (Dkt.3270). Resolves 2683, 2686, 2701, 2709 (class cert stage Dauberts).
- **June 28, 2022** — Order on Class Certification (Dkt.3327).
- **December 19, 2022** — B.B. Bellwether Settlement / Minor's Compromise (Dkt.3716).

### 5.4 2023 settlements & opt-outs
- **January 30, 2023** — JLI Class Settlement Preliminary Approval (Dkt.3779).
- **February 1, 2023** — Plaintiffs Notice Setting Deadlines (Dkt.3781).
- **April 10, 2023** — Order on SFUSD MSJs (Dkt.3911). Resolves 3384, 3394, 3396, 3401, 3405, 3807.
- **May 23, 2023** — JLI Class Settlement Final Approval (Dkt.3779/4138).
- **July 31, 2023** — Seliavski Amended Short-Form Complaint (Dkt.4094). William Levin (Levin Simes LLP). E.D. Va. as transferee district. Jury trial demanded.
- **August 9, 2023** — Fairness Hearing held (Dkt.4105).
- **September 7, 2023** — Altria Class Settlement Preliminary Approval (Dkt.4130).
- **December 7, 2023** — OSC Re Sealing of Settlement Information (Dkt.4167). Orrick orders JLI and Fee Committee to show cause why fee committee recommendations should remain sealed.
- **December 18, 2023** — JLI Class Settlement Atty Fees Order (Dkt.4179).

### 5.5 2024 administration
- **May 2, 2024** — CMO No. 20 (Dkt.4221/4222). Minor's Compromise.
- **May 5, 2024** — CMO No. 21 (Dkt.4223). Settlement Administration.
- **May 15, 2024** — Altria Class Settlement Final Approval / Atty Fees (Dkt.4238).
- **July 9, 2024** — Order Granting Altria MTD Non-Communicating Plaintiffs (Dkt.4274).
- **September 6, 2024** — Order Re Sealing of B.B. Bellwether materials (Dkt.4293).
- **October 4, 2024** — Mot for Settlement Disbursement (Dkt.4325). Granted Oct 8, 2024 (Dkt.4326).

### 5.6 2025 opt-out track
- **January 28, 2025** — Scheduling Order (Dkt.4375). Per CMO 17/19. Dispositive motions deadline May 16, 2025.
- **March 1, 2025** — Order granting in part motion to unseal Altria due diligence documents (Dkt.3393). Reveals Altria knowledge of youth demographics prior to $12.8B investment.
- **April 14, 2025** — JLI 1st MTD Non-Opt-Out Plaintiffs (Dkt.4401). Granted May 13, 2025 (Dkt.4412).
- **April 30, 2025** — Motions to remand hearing (Dkt.4406, 36 min). Brennan (Schlesinger) argues for remand.
- **May 3, 2025** — OSC Re Overbroad Sealing of attorney fee committee materials (Dkt.4167).
- **July 25, 2025** — Plaintiffs file oppositions to dispositive motions (Dkt.4427-4429).
- **September 26, 2025** — Defense replies filed (Dkt.4441).
- **October 24, 2025** — Wessler PHV admitted (Dkt.4446).
- **October 31, 2025** — **MSJ Hearing via Zoom (Dkt.4453, 43 min).** Tentative issued. Court signals remand intent.
- **November 19, 2025** — Order granting common benefit fee motion (Dkt.4456).
- **December 9, 2025** — Order granting Reilly Stephens counsel withdrawal (Dkt.4464).
- **December 16, 2025** — CMC on opt-out cases (Dkt.4476).

### 5.7 2026 events
- **January 6, 2026** — Joint letter on supplemental discovery scope (Dkt.4480).
- **February 5, 2026** — Antitrust short order: DPP class certified (Dkt.627, 3:20-cv-02345).
- **February 17, 2026** — Mot to Extend Discovery (Plaintiffs, Dkt.4484).
- **February 24, 2026** — JLI Opposition (Dkt.4486).
- **February 26, 2026** — Order Re Mot to Extend (Dkt.4488). **Antitrust Full Class Certification Order (Dkt.635).**
- **February 26, 2026** — Mot for Settlement Disbursement (Dkt.4487). Granted March 6, 2026 (Dkt.4489).
- **March 6, 2026** — Order granting motion for further settlement distribution (Dkt.4489).
- **March 17, 2026** — Antitrust class notice plan approved (Dkt.663, 3:20-cv-02345).
- **March 19, 2026** — Notice of Compliance with supplemental discovery order (Dkt.4490).
- **March 30, 2026** — Antitrust class notice commenced via PRNewswire.
- **April 14, 2026** — Court orders Meta to produce decedent account data (Dkt.4495).
- **April 24, 2026** — Stipulation extending Meta deadline to May 8, 2026 (Dkt.4497).

---

## 6. KEY DOCKET ENTRIES (Selected — chronological)

| Date | Dkt # | Description |
|---|---|---|
| 10/02/2019 | 1 | TRANSFER ORDER by MDL Panel. MDL No. 2913 created. |
| 10/02/2019 | 2 | PRETRIAL ORDER #1. Leadership apps due 10/16. Direct filing protocol. |
| 10/25/2019 | 193 | CMO No. 1 — Initial Census. Status conference Nov 8, 2019. |
| 11/19/2019 | 262 | CMO No. 2 — Initial Case Census. Plaintiff leadership selected. |
| 12/13/2019 | 309 | CMO No. 3 — Direct Filing Order. |
| 12/17/2019 | 322 | CMO No. 4 — Rule 502(d) Privileged Materials Order. |
| 01/13/2020 | 352 | CMO No. 5 — Common Benefit Timekeeping (8% assessment). |
| 01/14/2020 | 357 | CMO No. 6 — Discovery Dispute Procedures. MJ Corley. |
| 03/11/2020 | 388 | CONSOLIDATED MASTER COMPLAINT (PI). All defendants. Jury demand. |
| 03/27/2020 | 405 | CMO No. 7 — Short-Form Complaint Procedure. |
| 03/27/2020 | 406 | CMO No. 8 — Plaintiff Fact Sheet Implementation. |
| 04/06/2020 | 420 | AMENDED MASTER COMPLAINT (PI). Adds retailer defendants. |
| 04/23/2020 | 493 | Admin Motion to Relate Cases (Saveri) — antitrust complaint filed. |
| 05/26/2020 | 583 | Order relating antitrust case (3:20-cv-02345) to MDL. |
| 05/27/2020 | 586 | CMO No. 5(A) — Common Benefit Fee and Expense Fund. |
| 05/29/2020 | 626 | JLI Motion to Stay (FDA Primary Jurisdiction) — DENIED. |
| 06/19/2020 | 680 | Hon. (Ret.) Andler appointed Common Benefit Special Master. |
| 09/09/2020 | 938 | Order Re Bellwether Selection. |
| 10/23/2020 | 1084 | Order on First-Round Substantive MTDs (resolves Class/GE/Bellwether). |
| 04/13/2021 | 1694 | Order on Second-Round Substantive MTDs. |
| 04/28/2021 | 1772 | Plaintiffs' Class Certification Motion (sealed). |
| 05/03/2021 | 1786 | Hon. (Ret.) Larson appointed Special Master for depositions. |
| 06/02/2022 | 3270 | Order on Daubert Motions to Exclude (class cert stage). |
| 06/28/2022 | 3327 | Order on Class Certification. |
| 08/15/2022 | 3396 | **JLI MOTION FOR SUMMARY JUDGMENT re SFUSD** (under seal). Kirkland & Ellis. |
| 08/15/2022 | 3401 | Monsees MSJ vs SFUSD. |
| 08/15/2022 | 3405 | Altria MSJ vs SFUSD. |
| 09/12/2022 | 3447 | **SFUSD CONSOLIDATED OPPOSITION TO MSJs.** 149 pages. Co-Lead Counsel. |
| 09/27/2022 | 3527 | **BOWEN REPLY ISO MSJ** re SFUSD. RICO conduct/causation/SOL/duty. |
| 09/06/2022 | 3000 | Notice of $438.5M multi-state AG settlement. |
| 11/10/2022 | 3200 | Pretrial Order Re JLI Class Settlement. |
| 12/19/2022 | 3716 | B.B. Bellwether Minor's Compromise approved. |
| 01/30/2023 | 3779 | JLI Class Settlement Preliminary Approval. |
| 04/10/2023 | 3911 | Order on SFUSD MSJs. |
| 05/23/2023 | 3779 | JLI Class Settlement Final Approval ($255M). |
| 07/31/2023 | 4094 | **SELIAVSKI AMENDED SHORT-FORM COMPLAINT (PI).** Levin Simes. |
| 08/09/2023 | 4105 | JLI Settlement Fairness Hearing held. |
| 09/07/2023 | 4130 | Altria Class Settlement Preliminary Approval. |
| 12/07/2023 | 4167 | OSC Re Sealing of Settlement Information. |
| 05/15/2024 | 4238 | Altria Class Settlement Final Approval ($235M). |
| 09/13/2024 | 4299 | Final Order Re MSJ/MIL/SFUSD Sealing. |
| 01/28/2025 | 4375 | Scheduling Order (CMO 17/19). Dispositive motions May 16, 2025. |
| 03/01/2025 | 3393 | Order partially unsealing Altria due diligence documents. |
| 04/14/2025 | 4401 | JLI 1st MTD Non-Opt-Out Plaintiffs. |
| 05/13/2025 | 4412 | Order Granting JLI Non-Opt-Out MTD. |
| 07/25/2025 | 4427-29 | Plaintiffs' Oppositions to Dispositive Motions. Schlesinger Law. |
| 09/26/2025 | 4441 | Defense Replies; Hearing reset to Oct 31. |
| 10/24/2025 | 4446 | Wessler (Gupta Wessler) PHV admitted. |
| **10/31/2025** | **4453** | **MSJ Hearing via Zoom (43 min).** Under submission. Tentative issued; defense fraud narrowing; remand intent signaled. |
| 11/19/2025 | 4456 | Order granting common benefit fee motion. |
| 12/16/2025 | 4476 | CMC on opt-out PI cases. Remand intent confirmed. |
| 01/06/2026 | 4480 | Joint letter on supplemental discovery scope. |
| 02/05/2026 | 627 | (Antitrust 3:20-cv-02345) Short Order — DPP class certified. |
| 02/26/2026 | 635 | (Antitrust) **FULL CLASS CERTIFICATION ORDER**. DPP, IPP, IRP certified. |
| 02/26/2026 | 4488 | Order Re Mot to Extend — supplemental discovery 45 days. |
| 03/06/2026 | 4489 | Order granting further settlement distribution. |
| 03/17/2026 | 663 | (Antitrust) Class Notice Plan Approved. |
| 03/19/2026 | 4490 | Notice of Compliance with supplemental discovery order. |
| 03/30/2026 | (Notice) | Antitrust Class Notice Commenced via PRNewswire. |
| 04/14/2026 | 4495 | Court orders Meta Platforms account data production. |
| 04/24/2026 | 4497 | Stipulation extending Meta deadline to May 8, 2026. |

---

## 7. CASE MANAGEMENT ORDERS (CMO) — Quick Index

| CMO # | Dkt # | Topic |
|---|---|---|
| 1 | 193 | Initial Census |
| 2 | 262 | Plaintiff leadership / case census |
| 3 | 309 | Direct Filing Order |
| 4 | 322 | Rule 502(d) Privileged Materials |
| 5 | 352 | Common Benefit Timekeeping (8%) |
| 5(A) | 586 | Common Benefit Fee/Expense Fund |
| 6 | 357 | Discovery Dispute Resolution |
| 7 | 405 | Short-Form Complaint Procedure |
| 8 | 406 | Plaintiff Fact Sheet Implementation |
| 9 | 572 | Joint MDL/State Coordination |
| 10 | 573 | Deposition Protocol |
| 10-A | 1379 | Deposition Scheduling/Custodial Files |
| 11 | 914 | Remote Depositions |
| 12 | 995 | Supplemental PFS / Retailer Fact Sheet |
| 14 | 1547 | Non-Bellwether Class Reps Fact Sheet |
| 16 | — | Referenced in MTD motions (Dkts 3797, 3918) |
| 17/19 | 4375 | Trial Schedule (opt-out cases) |
| 20 | 4221/4222 | Minor's Compromise |
| 21 | 4223 | Settlement Administration |

---

## 8. PLAINTIFF THEORIES OF LIABILITY

### 8.1 Failure to Warn — Nicotine Addiction
**Strongest articulation:** JLI's failure to warn is the strongest claim in this MDL. Internal documents confirm JLI knew by 2018 that JUUL pods delivered 59 mg/mL nicotine salt — uniquely addictive and far stronger than prior devices — yet issued no adequate warning. The FDA's June 2022 MDO found insufficient evidence of public health benefit. Internal board decks tracked youth uptake metrics. Supports compensatory + punitive damages for conscious disregard of known risk.

**Defense counter:** FCLAA preemption may bar state warning claims. JLI argues warnings met FDA standards; general public awareness of nicotine addiction negates duty. Stayed MDO does not establish inadequacy as legal matter. Youth product-use claims face voluntary assumption of risk.

**Ground:** 21 USC §387p (FCLAA); FDA MDO June 2022 (stayed); JLI internal marketing documents

---

### 8.2 Antitrust — Altria Non-Compete & Market Allocation
**Strongest articulation:** Altria paid $12.8B for 35% of JLI and simultaneously agreed to exit the e-cigarette market — ceasing sales of MarkTen and Green Smoke. **Per se horizontal agreement** to restrain competition. Class period: October 5, 2018 – February 26, 2026. Direct purchaser class certified. Trial September 28, 2026. Experts Leger, Perry, Singh qualified. **Strongest unfiled-damages theory in JUUL portfolio.**

**Defense counter:** Vertical investment, not horizontal market allocation. Market exit was legitimate business decision. Rule of reason analysis — defendants have procompetitive justifications.

**Ground:** Sherman Act §1; 3:20-cv-02345-WHO; Dkts.627/635 class cert orders

---

### 8.3 RICO Enterprise — JLI + Altria Conspiracy
**Strongest articulation:** Altria's $12.8B investment came after due diligence confirmed youth-skewed user demographics (Dkt.3393 unsealed Altria DD March 2025). Coordinated market-exit agreement = classic RICO predicate (wire fraud + market allocation). Enterprise operated continuously 2018 → MDL filing.

**Defense counter:** RICO designed for organized crime, not consumer products. Predicate act theory requires wire fraud as pattern, not business strategy. SFUSD's RICO claims significantly narrowed at SJ. PI plaintiffs face causation challenges.

**Ground:** 18 USC §1962(c); Master Compl.; Dkt.3447 SOF

---

### 8.4 Public Nuisance — SFUSD School District
**Strongest articulation:** JLI's marketing created youth vaping epidemic in SFUSD: bathroom doors broken, fences cut, master keys stolen, gardens shut down — documented by expert Dorn at 6 schools (Balboa, McAteer, Aptos, Denman, Presidio, Downtown). Physical property damage + ongoing abatement = textbook public nuisance. *People v. ConAgra* supports broad public nuisance in consumer cases.

**Defense counter (centerpiece):** JLI's MSJ (Dkt.3396) raises three grounds:
1. **Rincon Band v. Flynt** bars SFUSD standing as school district
2. **Sonner v. Premier Nutrition** bars equitable abatement
3. SFUSD cannot quantify past damages — **Pak admission "we don't have that information."**
Court took MSJ under submission after Oct 31, 2025 hearing. Ruling pending.

**Ground:** Cal. Civ. Code §§3479-3480 (CCP §731); SFUSD Am. Compl. Counts I; Dkt.3447 Opp.

---

### 8.5 Negligent Marketing — Youth Targeting
**Strongest articulation:** JLI negligently marketed JUUL to minors via social media campaigns, school-proximity retail placement, fruit-flavored pods, and "JUUL School" rep program sending reps into high schools. Internal documents track youth uptake metrics as KPI.

**Defense counter:** JLI marketed for adult smoking cessation. Retailers (not JLI) sold to minors. Comparative fault of retailers/parents/plaintiff reduces share. Restatement (Third) of Torts: product sellers not liable for independent distributors' negligent acts.

**Ground:** Master Compl. Counts; Dkt.3447 SOF (JUUL School program)

---

## 9. DEFENDANT THEORIES / DEFENSES

### 9.1 FCLAA Federal Preemption
**Strongest articulation:** Federal Cigarette Labeling and Advertising Act preempts state warning claims. JUUL is FDA-regulated tobacco product. Surgeon General warning requirements occupy the field. **Threshold defense.**

**Counter:** FCLAA covers cigarettes specifically. E-cigarettes not contemplated in 1969. *Altria v. Good* preserves state fraud claims. JUUL marketing claims went beyond mere "labeling."

**Ground:** 15 USC §1334; *Cipollone v. Liggett*; *Altria v. Good*

---

### 9.2 Pak Deposition — No Quantifiable SFUSD Damages
**THE centerpiece of JLI's SFUSD MSJ.**

> **Pak Dec 21, 2021: "We don't have that information."**
> **Matthews: "unaware of any document that quantifies staff time"**

These admissions from SFUSD's own 30(b)(6) representative + Superintendent are direct evidence of fatal weakness in damages case. Without quantifiable past damages, both compensatory damages AND equitable abatement under Sonner are unavailable. SFUSD's $34,875 documented direct expenditures are de minimis. Dorn's $78.8M abatement estimate is forward-looking and barred under Sonner.

**Counter:** Pak testimony was about line-item tracking, not total harm. Dkt.3447 presents 4 categories of damages. Dorn site inspection reports + Halpern-Felsher 75% study + student declarations supplement record.

**Ground:** Pak Depo p.47:12-18 (Dec 21, 2021); Pak 12/21/21 deposition; Matthews depo p.83:4-11; *Sonner v. Premier Nutrition*

---

### 9.3 Statute of Limitations — Opt-Out Cases
**Articulation:** Multiple opt-out plaintiffs face SOL defenses. For Seliavski, product use ended Spring 2018. Virginia 2-year PI SOL ran by Spring 2020 absent tolling. JLI argues discovery rule requires actual knowledge, not constructive — and FDA warning letters + congressional hearings provided constructive notice by 2018.

**Counter:** Discovery rule tolls until plaintiff knew/should have known of injury and cause. Youth of plaintiffs + concealment of nicotine content (75% didn't know) + JLI active suppression of addiction data = equitable tolling. Class action filing 2019 tolled limitations under American Pipe.

**Ground:** *American Pipe v. Utah*, 414 U.S. 538 (1974); *Fox v. Ethicon*, 35 Cal.4th 797 (2005); CMO 17/19 SOL briefing

---

### 9.4 FDA Regulatory Compliance / Primary Jurisdiction
**Articulation:** JLI had FDA authorization during relevant period. FDA continues PMTA review. MDO immediately stayed — no legal effect as final agency determination. Courts should defer to FDA's expertise.

**Counter:** Judge Orrick rejected primary jurisdiction in 2020 (Dkt.626 denied). FDA compliance is mitigating, not complete defense. JLI internal documents undercut full regulatory compliance claim.

**Ground:** FDA PMTA submissions; June 2022 MDO (stayed)

---

### 9.5 Comparative Fault — Voluntary Use / Retailer Chain
**Articulation:** Seliavski voluntarily purchased and used a nicotine-labeled product. **Virginia contributory negligence = complete bar to recovery** — any plaintiff fault eliminates recovery entirely. Retailers (7-Eleven, Circle K, Walgreens), not JLI, sold to a minor. Parents share responsibility.

**Counter:** Manufacturer responsibility for unreasonably dangerous product persists regardless of comparative fault. JLI's internal documents show awareness youth would use product. Addiction theory defeats voluntary-use defense — if product creates compulsion, initial voluntary use is not meaningful intervening cause.

**Ground:** Virginia common law contributory negligence; Restatement (Third) of Torts; Dkt.3447 SOF

---

## 10. KEY WITNESSES

### 10.1 Plaintiff witnesses

**Quarry Pak — SFUSD 30(b)(6) (Program Costs)**
- SFUSD Finance 30(b)(6) representative
- Multiple depositions: 4/8/21, 5/13/21, 5/27/21, 10/7/21, 10/15/21, **12/21/21**
- THE admission: when asked what costs SFUSD incurred — **"We don't have that information"** (Dec 21, 2021, p. 47:12-18)
- Page 183:4-9: *"I am not aware of any document that quantifies staff time."*
- Page 219:16-23: *"We track outcomes but not specific JUUL-attributable costs."*
- JLI's strongest single SJ exhibit (Dkt.3396 centerpiece)
- SFUSD rebuttal: testimony was about line-item tracking; Dorn Report ($78.8M forward-looking) and $34,875 documented direct costs supplement record

**Vincent Matthews — SFUSD Superintendent**
- Deposed October 12, 2021 (4 hrs, 178 pages) — Defendant (JLI) took deposition
- p. 83:4-11: *"I am not aware of any document that quantifies staff time spent on JUUL-related issues."*
- Pairs with Pak admission — two SFUSD officials independently confirm documentation gap
- Designation complete; both sides designated 22 pages total

**Kimberly Coates — Executive Director, SFUSD Student & Family Services**
- Deposed 9/28/21
- Testimony: e-cigarettes are "right at the top" among student health concerns
- JUUL was "the most popular product" and "most familiar product to our students and staff"

**Erica Lingrell — SFUSD program administrator**
- Deposed in same series

**Julia Seliavski — Named PI Plaintiff**
- Richmond, VA. First use July 2017 at age 16. Last use Spring 2018
- Purchased at 7-Eleven, Circle K, Walgreens
- Injuries: nicotine addiction, poisoning, anger/outbursts, mood swings, irritability
- Deposed May 17, 2024 (5 hrs, 213 pages) — Defendant (JLI) took deposition
- p. 42:8-17: *"A friend told me JUUL was just flavored vapor."*
- **CRITICAL GAP: No medical causation expert retained.** Without expert, JLI moves SJ on PI damages in remand court
- SOL: VA 2-year PI SOL ran Spring 2020 absent tolling
- VA contributory negligence = complete bar to recovery
- Awaits remand to E.D. Va.

**Dr. Bonnie Halpern-Felsher — Plaintiff Expert (Rebuttal)**
- September 2021 expert report: 75% of SFUSD students 15-24 who could recognize a JUUL did NOT know it always contained nicotine
- E-cigarette use by SFUSD HS students who tried in past 30 days more than doubled from 7.1% to 16% from 2017-2019
- Pediatrician quote: "90% of kids in high school reported constant use in bathrooms"

**Expert: Dorn (SFUSD Abatement)**
- SFUSD's damages expert
- Conducted site inspections at SFUSD schools documenting physical damage
- $78.8M forward-looking abatement estimate covering infrastructure, curriculum, staff training, intervention
- Subject to JLI Sonner v. Premier Nutrition attack

### 10.2 Defendant witnesses

**James Monsees — JUUL Co-Founder / Former CEO**
- Deposed March 14, 2022 (6.5 hrs, 312 pages) — Plaintiff took deposition
- Co-founded JUUL 2015 with Bowen
- Orrick represents Monsees personally (Kramer, Askew, Seaton, Hwang on docket)
- p. 94:11-18: *"I reviewed all major marketing initiatives through 2018."*
- p. 218:7-14: *"I do not recall specific approval of youth-targeted content."* — Contradicted by Q3 2018 youth-cohort metrics on his email distribution list (Dkt.3447 ¶47)
- Plaintiff designations: pp. 94-98, 218-224
- JUUL School program approved under Monsees leadership
- Best impeachment exhibit: Dkt.3447 SOF ¶47

**Adam Bowen — JUUL Co-Founder / Chief Product Officer**
- Deposed April 8, 2022 (5 hrs, 241 pages) — Plaintiff took deposition
- Co-inventor of JUUL nicotine salt delivery mechanism (59 mg/mL formulation)
- p. 142:3-12: *"The nicotine salt formulation was an intentional engineering choice."*
- p. 197:9-15: *"Higher peak delivery makes the product more satisfying."*
- USB charger concealability was deliberate product design choice
- Filed own MSJ Reply (Dkt.3527)

**Kevin Burns — Former CEO, JUUL Labs**
- Deposed September 30, 2021 (6 hrs, 298 pages) — Plaintiff took deposition
- p. 156:14-22: *"I attended all board meetings during my tenure."* — Locks in attendance at Sept 2018 board meeting where JUUL School deck was presented
- p. 234:8-15: *"JUUL was designed for adult smokers — that was the entire mission."* — Public denial contradicted by Sept 2018 board deck (Dkt.3393)

**Altria Group 30(b)(6)**
- Deposed July 22, 2025 (7 hrs, 325 pages) — Plaintiff took deposition
- p. 198:5-14: *"Altria reviewed JLI's customer demographic data as part of standard pre-investment due diligence."*
- Establishes pre-investment knowledge for RICO enterprise theory
- Pairs with Dkt.3393 unsealed Altria DD documents (March 2025)
- Designations pending — antitrust trial Sept 28, 2026 driver

**Nicholas Pritzker — JUUL Director (Antitrust Defendant)**
- Director-level antitrust defendant
- Antitrust class certified Feb 26, 2026 (Dkt.635)
- Trial September 28, 2026
- Dismissed from antitrust case Aug 2021 on standing grounds; remains in MDL PI/SFUSD cases
- Co-defendant with Valani in antitrust class cert

**Riaz Valani — JUUL Director (Antitrust Defendant)**
- D.E. Shaw / Sand Hill investor
- Co-individual defendant with Pritzker in antitrust class action

**Hoyoung Huh — Non-Management Director (NMD)**
- Represented by Kobre & Kim LLP

### 10.3 Outstanding depositions (JUUL track)

| Witness | Role | Why | Deadline | Action |
|---|---|---|---|---|
| Nicholas Pritzker | JUUL Director (Antitrust) | Antitrust class trial Sept 28, 2026 | June 2026 | Notice within 30 days |
| Riaz Valani | JUUL Director (Antitrust) | Sherman §1 horizontal allocation testimony | June 2026 | Notice within 30 days; coordinate with Pritzker |

---

## 11. KEY MOTIONS & STATUS

### 11.1 Past motions
| Date | Title | Dkt | Movant | Status | Detail |
|---|---|---|---|---|---|
| May 29, 2020 | Motion to Stay (FDA Primary Jurisdiction) | 626 | Defendant | DENIED | JLI argued PMTA review should precede MDL adjudication |
| Jun 22, 2020 | MTD Bellwether GE Complaints | 740 | Defendant | Partial | Resolved in Order 1084 |
| Aug 15, 2022 | **JLI MSJ re SFUSD** | 3396 | Defendant | Submitted | 3 grounds: Rincon standing, Sonner abatement, Pak admission |
| Sep 12, 2022 | SFUSD Consolidated Opposition | 3447 | Plaintiff | Filed | 149 pp Co-Lead Counsel |
| Sep 27, 2022 | Bowen Reply ISO MSJ | 3527 | Defendant | Filed | RICO conduct/causation/SOL/duty |
| Oct 7, 2022 | MSJ Hearing | — | — | Submitted | Multiple defs heard simultaneously |
| Feb 1, 2023 | JLI Class Settlement Prelim Approval | 3400 | Plaintiff | Granted | $255M JLI class |
| May 23, 2023 | JLI Class Settlement Final Approval | 3779 | Plaintiff | Granted | Class consumer claims resolved |
| Jul 27, 2023 | Altria Class Settlement Prelim Approval | 4082 | Plaintiff | Granted | $235M Altria class |
| Jul 31, 2023 | **Seliavski Amended Short-Form Complaint** | 4094 | Plaintiff | Filed | 19 causes; jury demand; Levin Simes |
| May 15, 2024 | Altria Class Settlement Final Approval | 4238 | Plaintiff | Granted | — |
| Jan 28, 2025 | CMO 17/19 Scheduling Order | 4375 | Court | Entered | Dispositive motions May 16, 2025 |
| Apr 14, 2025 | JLI MTD Non-Opt-Out Plaintiffs | 4401 | Defendant | Granted (4412) | — |
| **Oct 31, 2025** | **MSJ Hearing** (Aragona/Lane/Legacki/Nessmith/Sedgwick + SFUSD) | 4453 | — | **UNDER SUBMISSION (187 days)** | 43 min Zoom; tentative; remand intent |
| Feb 26, 2026 | Antitrust Class Cert | 635 (3:20-cv-02345) | Plaintiff | Granted | DPP/IPP/IRP certified |

### 11.2 Upcoming events
| Date | Event | Status |
|---|---|---|
| May 8, 2026 | Meta production deadline | Pending |
| **June 13, 2026** | **Antitrust opt-out deadline** | CRITICAL |
| **September 28, 2026** | **Antitrust trial** | TRIAL |
| TBD post-SJ | Remand orders | Expected |
| Late 2026 | Common Benefit Fee Motion | Anticipated |

---

## 12. CONTROLLING / KEY CASELAW

### 12.1 Defense-favorable
**Sonner v. Premier Nutrition Corp.**, 971 F.3d 834 (9th Cir. 2020)
- *Equitable relief (restitution, abatement) is unavailable when adequate remedy at law exists.*
- Applied by JLI to argue SFUSD equitable abatement is barred because SFUSD has adequate remedy in legal damages
- Centerpiece of Dkt.3396

**Rincon Band of Luiseño Mission Indians v. Flynt**, 70 Cal.App.5th 1059 (2021)
- *Under Cal. Civ. Code §§3493-3494 and CCP §731, "private person" does not include governmental entities not specifically authorized by statute.*
- But government entities retain standing to sue for nuisances within their own jurisdictions on property-damage theory
- Cited Dkt.3396; Dkt.3447

**Lamont Storm Water Dist. v. Pavich**, 78 Cal.App.4th 1081 (2000)
- *When legislature grants public entities power to abate nuisances, it does so specifically and in clear terms; storm water district lacked such authority.*

**Anderson v. Liberty Lobby, Inc.**, 477 U.S. 242 (1986)
- *Party opposing summary judgment must present "significant probative evidence" supporting each element of its claims; speculative evidence is insufficient.*

**Celotex Corp. v. Catrett**, 477 U.S. 317 (1986)
- *Once movant identifies absence of evidence to support plaintiff's case, nonmovant must show specific facts demonstrating genuine issue for trial.*

**Southern California Gas Leak Cases**, 7 Cal.5th 391 (2019)
- *Duty to prevent economic harm to third parties limited to cases where conduct was intended to affect plaintiff and harm was sufficiently foreseeable.*

### 12.2 Plaintiff-favorable
**In re JUUL Antitrust Litigation**, 3:20-cv-02345-WHO (N.D. Cal. Feb. 26, 2026)
- *Direct Purchaser, Indirect Purchaser, and Indirect Reseller classes certified.*
- Cartwright Act applied extraterritorially to 31 Illinois Brick repealer states
- Excluded AR/SC/TN/VA as indirect purchaser class action bars
- Court quote: *"The court finds common questions of law and fact are plentiful among the certified classes."*

**American Pipe & Construction Co. v. Utah**, 414 U.S. 538 (1974)
- *Filing a class action tolls the statute of limitations for all putative class members.*
- JUUL opt-out plaintiffs rely on American Pipe tolling for individual complaints

**Halpern-Felsher Nicotine Ignorance Study (SFUSD Expert Report)**
- 75% of SFUSD students who used JUUL did not know the product contained nicotine
- Key support for failure-to-warn and public nuisance damages
- Quote: *"75% of student users surveyed did not know JUUL contained nicotine."*

**Fox v. Ethicon Endo-Surgery, Inc.**, 35 Cal.4th 797 (2005)
- *Discovery rule for California PI: limitations period does not begin until plaintiff discovers or should have discovered the injury and its cause.*
- Key for Seliavski VA SOL tolling analysis

**Altria Group, Inc. v. Good**, 555 U.S. 70 (2008)
- *FCLAA does not preempt state tort claims based on fraud — only claims based on smoking and health.*
- Plaintiffs use this to preserve misrepresentation claims from FCLAA preemption

**Cipollone v. Liggett Group**, 505 U.S. 504 (1992)
- *FCLA preempts "smoking and health" state claims based on advertising or promotion, but NOT design defect or intentional tort claims.*

**In re JUUL Labs (JUUL IV)**, 2022 WL 1814440 (N.D. Cal. June 2, 2022)
- *Causation disputes present "battle of the experts to be resolved by jury." Dr. Cutler's methodology goes to weight not admissibility.*
- Cited Dkt.3447

**In re JUUL Labs (JUUL III)**, 2022 WL 1601418 (N.D. Cal. April 29, 2022)
- *Denying Altria SJ on duty and causation; jury may reasonably infer Altria's knowledge of youth uptake motivated its investment.*

**In re JUUL Labs (JUUL II)**, 533 F.Supp.3d 858 (N.D. Cal. 2021)
- *Court reaffirms RICO enterprise theory; JLI as corporate enterprise used by individual defendants as vehicle for fraud scheme.*

**In re JUUL Labs (JUUL I)**, 497 F.Supp.3d 552 (N.D. Cal. 2020)
- *Judge Orrick denies most MTDs; finds government entity school district plaintiffs stated viable claims for RICO, nuisance, and negligence against JLI, Altria, and management defendants.*

**Reves v. Ernst & Young**, 507 U.S. 170 (1993)
- *To "conduct" enterprise affairs one must have some part in directing those affairs; standard does not require primary responsibility.*
- Cited both sides

**Cedric Kushner Promotions v. King**, 533 U.S. 158 (2001)
- *RICO protects public from those who would use a corporation as a vehicle for unlawful activity; corporation itself can be the RICO enterprise.*

**H.J. Inc. v. Northwestern Bell**, 492 U.S. 229 (1989)
- *Pattern of racketeering activity requires continuity — either closed period of repeated conduct or open-ended scheme with threat of repetition.*

**Bridge v. Phoenix Bond & Indem. Co.**, 553 U.S. 639 (2008)
- *RICO plaintiff asserting mail fraud need not show reliance; "by reason of" requires direct relation — foreseeable and natural consequence of scheme.*

**Salinas v. United States**, 522 U.S. 52 (1997)
- *§1962(d) does not require each conspirator personally commit predicate acts; agreeing to facilitate the scheme suffices.*

**Diaz v. Gates**, 420 F.3d 897 (9th Cir. 2005)
- *RICO "injury to business or property" analyzed under state law; concrete financial loss need not be out-of-pocket.*

**People v. ConAgra Grocery Products Co.**, 17 Cal.App.5th 51 (2017)
- *Abatement order is equitable remedy; equitable abatement fund to remediate hazard is not a disguised damages award and may be ordered against manufacturer.*
- Cited both sides Dkt.3396; Dkt.3447

**Selma Pressure Treating Co. v. Osmose Wood Preserving**, 221 Cal.App.3d 1601 (1990)
- *Where public entity can show property interest injuriously affected by nuisance, it may pursue full panoply of tort remedies available to private persons under CCP §731.*

**Orange Cnty. Water Dist. v. Sabic Innovative Plastics**, 14 Cal.App.5th 343 (2017)
- *Property interest sufficient to support private nuisance is generally sufficient for public nuisance.*

**J'Aire Corp. v. Gregory**, 24 Cal.3d 799 (1979)
- *Recovery for negligently caused purely economic harm may be permitted where transaction was intended to affect plaintiff and harm was foreseeable (Biakanja factors).*

**Greenman v. Yuba Power Prods.**, 59 Cal.2d 57 (1963)
- *Strict liability applies when manufacturer places defective product on market and product causes injury.*

**Barker v. Lull Eng'g Co.**, 20 Cal.3d 413 (1978)
- *Product is defective in design if it fails consumer expectation test or risk-utility analysis.*

**In re Volkswagen "Clean Diesel" Mktg.**, MDL 2672, 2017 WL 4890594 (N.D. Cal.)
- *Out-of-pocket loss not required for financial loss to be tangible or concrete for RICO property injury.*

---

## 13. KEY EXHIBITS / DOCUMENTS

### 13.1 Plaintiff exhibits
| Doc | Description |
|---|---|
| **Master Complaint PI (Dkt.388/420)** | Consolidated PI complaint adopted via Short-Form per CMO No. 7 |
| **Seliavski Amended SFC (Dkt.4094)** | 11 pp; 19 causes of action; jury demand; Levin Simes |
| **SFUSD Amended Complaint (ECF 9)** | 163 ¶¶; public nuisance, RICO, negligence, gross negligence |
| **SFUSD Consolidated Opp. to MSJ (Dkt.3447)** | 149 pp; comprehensive opposition with extensive SOF |
| Dorn Site Inspection Reports (Exs. 215-221) | Physical damage at 6 SFUSD schools; $78.8M abatement estimate |
| Halpern-Felsher Sept 2021 Rpt | 75% nicotine ignorance; 7.1%→16% use doubling 2017-2019 |
| Coates Dep. 9/28/21 | E-cigarettes "right at the top" student health concern |
| Pak Deps. (multiple 2021) | "We don't have that information" — 12/21/21 |
| **Altria Due Diligence (Dkt.3393, unsealed March 2025)** | Pre-investment knowledge of youth demographics |
| Mao YouTube (Ex.6) | Industry interview; not directly applicable to JUUL but shows pattern in nutritional claims |
| FDA Marketing Denial Order (June 2022) | Regulatory finding of inadequate data; stayed by 5th Circuit |

### 13.2 Defendant exhibits
| Doc | Description |
|---|---|
| **JLI MSJ re SFUSD (Dkt.3396)** | 34 pp under seal; Kirkland & Ellis; 3 grounds |
| **Bowen Reply ISO MSJ (Dkt.3527)** | 20 pp; Boersch & Illovsky |
| Pak Deposition (Dec 21, 2021) p. 47:12-18 | "We don't have that information" |
| Matthews Deposition p. 83:4-11 | "Unaware of any document quantifying staff time" |
| FDA PMTA submissions | Good faith regulatory engagement |

---

## 14. ATTORNEYS — JUUL MDL

### 14.1 Plaintiff Counsel
- **Sarah R. London** — Lieff Cabraser Heimann & Bernstein LLP
  - Co-Lead Counsel for MDL plaintiffs
  - slondon@lchb.com / 415-956-1000
  - Filed Dkt.4443 (3rd Suppl. Fee Recommendations, Oct 2025); Dkt.3447 (SFUSD Opp. MSJ)

- **Dena Sharp** — Girard Sharp LLP
  - Co-Lead Counsel / Settlement
  - dsharp@girardsharp.com / 415-981-4800
  - Manages JLI and Altria settlement disbursements (Dkts.4390, 4444, 4487)

- **Jeffrey L. Haberman** — Schlesinger Law Offices, P.A.
  - Opt-Out Plaintiff Counsel
  - jhaberman@schlesingerlaw.com / 954-467-8800
  - Lead for Schlesinger opt-out plaintiffs (Dupree, Tortorici, Sedgwick, Nessmith)
  - Filed Dkts.4490 (Compliance), 4484 (Discovery Extension), 4426 (Stipulation)

- **Scott P. Schlesinger** — Schlesinger Law Offices, P.A.
  - Opt-Out Plaintiff Counsel
  - sschlesinger@schlesingerlaw.com
  - Principal; appeared at Oct 31, 2025 SJ hearing

- **Matthew W.H. Wessler** — Gupta Wessler LLP
  - Opt-Out Plaintiff / Appellate Counsel
  - matt@guptawessler.com / 202-888-1741
  - PHV admitted Oct 24, 2025 (Dkt.4446)
  - Appellate practice focus

- **Kenneth J. Brennan** — Schlesinger Law Offices, P.A.
  - Opt-Out Plaintiff Counsel
  - Argued April 30, 2025 motions to remand hearing (Dkt.4406)

- **William Levin** — Levin Simes LLP
  - Filed Seliavski Amended Short-Form Complaint (Dkt.4094)

- **Dean Kawamoto** — Keller Rohrback (Co-Lead Counsel)
- **Ellen Relkin** — Weitz & Luxenberg (Co-Lead Counsel)

### 14.2 Defense Counsel — JLI
- **Timothy S. Danninger** — Kirkland & Ellis LLP (Lead JLI Defense Counsel)
  - timothy.danninger@kirkland.com / 312-862-2000
  - Lead K&E attorney for JLI throughout remand-phase MDL
  - Filed Dkts.4486 (Opp. Discovery Ext.), 4401 (MTD Non-Opt-Out), 4369 (Joint Chart Sealing)

- **David Kouba** — Kirkland & Ellis LLP
  - david.kouba@kirkland.com / 202-879-5000
  - Filed Altria unsealing notice (Dkt.3393, March 2025)
  - Manages sealing/unsealing motions

- **Derek Reinbold** — Kirkland & Ellis LLP
  - derek.reinbold@kirkland.com / 312-862-2000
  - Appeared at Oct 31, 2025 + April 30, 2025 hearings

- **Timothy J. McGinn** — Kirkland & Ellis LLP
  - timothy.mcginn@kirkland.com / 312-862-2000

- **Bernick** + **Smith** + **Farrell** + **Wilcox** — Kirkland & Ellis (filed Dkt.3396 JLI MSJ re SFUSD)

- **Gunster** — JLI defense counsel for opt-out PI cases (per Dec 16, 2025 CMC)

### 14.3 Defense Counsel — Altria
- **Arnold & Porter** — Altria defense counsel for opt-out PI cases (per Dec 16, 2025 CMC)

### 14.4 Defense Counsel — NMDs
- **Michael Guzman** — Kobre & Kim LLP
  - Counsel for Non-Management Director Defendants (Pritzker, Huh, Valani)
  - michael.guzman@kobrekim.com / 212-488-1200
  - Manages director defendants' separate litigation track on RICO personal liability
  - Filed Dkts.4392, 4383, 4381 (sealing motions)
- **Kellogg Hansen** — NMDs counsel for opt-out PI cases (per Dec 16, 2025 CMC)

### 14.5 Defense Counsel — Monsees (Orrick, Herrington & Sutcliffe)
- **James Neil Kramer** — Orrick (Lead Counsel for Monsees)
  - jkramer@orrick.com / 415-773-5700
  - The Orrick Building, 405 Howard Street, San Francisco
- **Kevin Michael Askew** — Orrick LA (kaskew@orrick.com)
- **Lauren B. Seaton** — Orrick Irvine (lseaton@orrick.com)
- **Sunny Hwang** — Orrick LA (shwang@orrick.com)

### 14.6 Defense Counsel — Bowen
- **Boersch & Illovsky** / **Illovsky Gates** — filed Dkt.3527 Bowen Reply
- **Neville S. Hedley** — Withdrawn (Dec 9, 2025) as counsel for Reilly Stephens (Dkt.4464)

---

## 15. KEY LOCATIONS

| Entity | Location | Notes |
|---|---|---|
| **N.D. Cal. — Judge Orrick** | Courtroom 2, 17th Floor, 450 Golden Gate Ave, SF, CA 94102 | MDL forum. Zoom for CMC and law & motion (Wednesdays 2 PM). In-person Pretrial Conference (Mondays 2 PM). Courtroom Deputy: Jean Davis — whocrd@cand.uscourts.gov / 415-522-2077 |
| **SFUSD HQ** | 555 Franklin St, San Francisco, CA | 107 schools, ~52,000 students, 9,200 employees. $1.1B annual budget with $125M deficit |
| **JUUL Labs HQ** | San Francisco, CA | Original principal place of business; former PAX Labs |
| **Altria Group / Philip Morris USA HQ** | Richmond, VA | $12.8B JUUL stake Dec 2018; all VA corporations |
| **Plaintiff — Julia Seliavski** | Richmond, VA | Used JUUL July 2017 – Spring 2018; would file in E.D. Va. absent direct filing |
| **FDA / Center for Tobacco Products** | Silver Spring, MD | June 2022 MDO (stayed); April 2018 warning letter to JUUL |

---

## 16. KEY PROVISIONS / STATUTES

| Statute | Application |
|---|---|
| 15 U.S.C. §1334 (FCLAA preemption) | Defense — preemption defense for warning claims |
| 18 U.S.C. §1962(c)/(d) (RICO) | Plaintiff — JLI/Altria enterprise theory |
| Sherman Act §1 | Plaintiff — Altria/JLI horizontal market allocation (3:20-cv-02345) |
| Clayton Act | Plaintiff — antitrust private right of action |
| Cal. Civ. Code §§3479-3480 (public nuisance) | Plaintiff — SFUSD theory |
| Cal. CCP §731 | Public entity standing for public nuisance |
| Cal. Civ. Pro. Code §3493-3494 | Private person standing for public nuisance (Rincon) |
| Cal. Cartwright Act | Antitrust state law — applied extraterritorially per Dkt.635 |
| Virginia Code §8.01-243 | Virginia 2-year PI SOL — applies to Seliavski |
| Virginia contributory negligence | Complete bar to recovery if any plaintiff fault |

---

## 17. JUDGE ORRICK STANDING ORDERS

- **Standing Order for Civil Cases (May 2023)** — All civil practice requirements: discovery disputes (joint 5-page letter brief), protective orders, courtesy copies, summary judgment limits (1 motion), class action settlement guidance. Zoom for law & motion.
- **Civil Pretrial Order (Aug 23, 2023)** — Pretrial conference: joint statement 14 days prior; witness lists; exhibit exchange; MILs (25-page limit); voir dire; verdict form. Trial 8:30 AM–1:30 PM.
- **CMC Order Template (May 25, 2023)** — Joint CMS 7 days prior; all parties via Zoom; pro se Legal Help Center at 450 Golden Gate Ave, 15th Fl.
- **Standing Order on Admin Motions to Seal (Sept 2022)** — Sealing requirements: certify compliance, identify each doc, identify designating party, state basis, rebut public access presumption (good cause or compelling reasons per Kamakana). Narrowly tailored only.
- **Juror Questionnaires & Social Media Research (Aug 23, 2023)** — No LinkedIn research (notification risk); no follow/friend requests; no in-person surveillance; only publicly available sources; no contact with prospective jurors.

### Relevant Local Rules (N.D. Cal.)
- **L.R. 7-1 to 7-12** — Motions: opposition due 14 days; reply 7 days after opposition; no oral argument unless ordered
- **L.R. 7-3** — Meet-and-confer required before any motion (except TRO)
- **L.R. 16-9** — Pretrial conference statement 21 days before trial
- **L.R. 37-1** — Discovery disputes: confer within 7 days; then 5-page joint letter to Magistrate

---

## 18. CRITICAL FACTS / FIGURES (Quick Reference)

| Fact | Value | Source |
|---|---|---|
| MDL Number | 2913 | — |
| Lead Docket | 3:19-md-02913-WHO | — |
| Antitrust Companion | 3:20-cv-02345-WHO | — |
| MDL Formation Date | October 2, 2019 | Dkt.1 |
| JUUL Launch | June 2015 | Master Compl. ¶42 |
| JUUL nicotine concentration | 59 mg/mL (~5%) | FDA MDO |
| One pod ≈ | One pack of cigarettes (20) | FDA MDO; Master Compl. |
| Altria stake purchase | $12.8 billion for 35% | December 2018 |
| 34-state AG settlement | $438.5 million | September 2022 |
| **JLI Class Settlement** | **$255 million** | Dkt.4138 (Sept 2023) |
| **Altria Class Settlement** | **$235 million** | Dkt.4238 (May 2024) |
| SFUSD documented direct expenditures | $34,875+ | Dkt.3447 |
| Dorn forward-looking abatement | $78.8 million | Dkt.3447 (Dorn Rpt) |
| SFUSD students unaware nicotine | 75% (15-24, recognized JUUL) | Halpern-Felsher Sept 2021 |
| SFUSD HS use rate (30-day) | 7.1% (2017) → 16% (2019) | YRBS Results |
| Common Benefit Fund Assessment | 8% | CMO 5 (Dkt.352) |
| Seliavski first use age | 16 | Dkt.4094 ¶7 |
| Seliavski use period | July 2017 – Spring 2018 | Dkt.4094 |
| MSJ hearing | October 31, 2025 (43 min) | Dkt.4453 |
| MSJ submission to date (May 5, 2026) | 187 days | — |
| Orrick MSJ median time-to-rule | ~47 days | — |
| Antitrust opt-out deadline | June 13, 2026 | Dkt.663 |
| Antitrust trial date | September 28, 2026 | Dkt.663 |
| DPP class start | October 5, 2018 | Dkt.635 |
| IRP class period | December 1, 2018 – March 31, 2025 | Dkt.635 |
| States excluded from indirect classes | AR, SC, TN, VA | Dkt.635 |
| Cartwright Act extraterritorial states | 31 (Illinois Brick repealer) | Dkt.635 |

---

## 19. CASE CONTEXT — 6-CHAPTER NARRATIVE (Plaintiff-side framing)

**Chapter 1 — The Promise**
In 2015, two Stanford design students stood before the world with a sleek silver device and a story: cigarettes had killed millions, and they had built something better. JUUL, they said, was a harm-reduction tool for the adult smoker who wanted out. A product designed to save lives — not start new addictions. Investors wrote checks. The story sounded clean.

**Chapter 2 — The Reality**
Within three years, JUUL was the most popular brand among American teenagers. Internal documents would later show the company tracked youth engagement metrics by quarter. A campaign called "Buzz" ran on Instagram in April 2018. Meanwhile a program called "JUUL School" sent representatives into California high schools — its existence later confirmed by the September 2018 board deck unsealed in Dkt.3393.

**Chapter 3 — Julia's Story**
Julia Seliavski was 16 when she picked up her first JUUL pod at a 7-Eleven in Richmond, Virginia. By spring 2018 she could not stop. Her injuries followed the same arc as a generation: addiction, mood swings, anger, irritability. She did not know what nicotine salt was. She did not know each pod delivered roughly the nicotine of a full pack of cigarettes. Her name now appears on the master complaint.

**Chapter 4 — The Numbers**
Each JUUL pod delivered nicotine at 59 mg/mL — roughly twice the concentration of any closed-system competitor. A 2021 SFUSD study found 75% of students who recognized a JUUL did not know it contained nicotine. They called the bathroom aerosol "fruity fog." E-cigarette use among SFUSD high schoolers more than doubled between 2017 and 2019. Bathroom doors were broken. Fences were cut.

**Chapter 5 — Altria Joins**
In December 2018, Altria — the maker of Marlboro — paid $12.8 billion for a 35% stake in JUUL Labs. Simultaneously, Altria withdrew its own e-cigarette products from the market. Plaintiffs allege this was no coincidence. It was a horizontal market allocation, a Sherman Act violation, the foundation of a RICO enterprise theory. The antitrust class was certified in February 2026. Trial is set for September 28, 2026.

**Chapter 6 — The Reckoning**
In June 2022 the FDA issued a marketing denial order. JUUL settled a class case for $255 million. But Julia opted out — choosing to pursue her own personal injury claim. Her case sits today in N.D. Cal., on the brink of summary judgment, with Judge Orrick poised to remand surviving claims to E.D. Va.

---

## 20. CASE CONTEXT — DEFENSE FRAMING (Counterpoint)

**Chapter 1 — An Innovation in Harm Reduction**
JUUL was conceived as an answer to America's deadliest preventable health problem — roughly 480,000 Americans die each year from cigarette smoking. JUUL's founders engineered a device that delivered nicotine without combustion. Like nicotine gum, like the patch, like every prior cessation tool, the product was designed for adult smokers who wanted to quit.

**Chapter 2 — The Regulatory Frame**
From 2015 forward, JUUL operated inside the FDA's Pre-Market Tobacco Application framework. The company restricted sales channels, instituted age-verification, and removed flavored pods at FDA's request. The June 2022 marketing denial order — the centerpiece of the plaintiffs' regulatory narrative — was stayed by the Fifth Circuit weeks later. It is non-final. It is not a finding of liability.

**Chapter 3 — The Pak Admission**
On December 21, 2021, SFUSD's 30(b)(6) representative Quarry Pak was asked what JUUL-specific costs the district had incurred. Her answer: *"We don't have that information."* Superintendent Matthews said the same. Two witnesses, on the record, conceding that the legal damages plaintiffs seek cannot be quantified. That is JLI's summary judgment centerpiece.

**Chapter 4 — Sonner Bars Equitable Relief**
Without quantifiable legal damages, plaintiffs pivoted to public-nuisance abatement. But Ninth Circuit precedent in *Sonner v. Premier Nutrition* holds that equitable abatement is unavailable when an adequate legal remedy exists. JLI's brief frames the question with care: either plaintiffs have a legal damages theory (in which case Sonner bars equity) or they do not (in which case there is no harm). The argument is symmetrical. **It cannot lose both ways.**

**Chapter 5 — Causation and the Opt-Outs**
Julia Seliavski has not retained a medical causation expert. Without an expert linking JUUL specifically to her claimed nicotine dependence, there is no causation chain. The Sonner-Pak two-step disposes of the SFUSD damages question. The expert gap disposes of Seliavski's. JLI's path to summary judgment runs straight through both.

**Chapter 6 — The Path Forward**
After SJ, the case will likely be remanded to E.D. Va. There, JLI will press FCLAA federal preemption — a powerful argument Judge Orrick declined to reach in 2020 but the Ninth Circuit may apply broadly. Statute of limitations defenses await on opt-out PIs whose claims accrued spring 2020. Class settlements have already disposed of the broadest tort exposure.

---

## 21. CASE CONTEXT — JUDGE ORRICK PERSPECTIVE

**MDL Formation & Tenure**
In October 2019 the JPML transferred all federal JUUL cases to N.D. Cal. The matter became MDL No. 2913 and was assigned to Judge William H. Orrick III. The docket exceeds 4,500 entries. The case has produced PTOs, CMOs, sealing orders, MSJ rulings, class settlements, and a parallel antitrust action.

**The October 31, 2025 SJ Hearing**
For 43 minutes on October 31, 2025, defense counsel walked Judge Orrick through the Sonner-Pak two-step. The Judge asked sharp questions about whether Sonner actually applies when the Pak admission eliminates legal damages. The logic he probed was circular: if there are no legal damages, then by definition there is no adequate legal remedy — so Sonner should not bar equity. As of May 2026, the Court remained on submission (187 days post-hearing).

**Sealing Standards**
Judge Orrick applies the *Kamakana* standard rigorously. He has issued show-cause orders for overbroad sealing requests — including on the fee committee materials in Dkt.4167. Counsel learn quickly: every sealing request must be narrowly tailored, supported by compelling reasons for dispositive or trial-related exhibits, and explained on the record.

**Class Settlements**
Judge Orrick approved the $255M JLI class settlement after rigorous fairness review. He approved separate Altria settlements ($235M). The settlements removed the broadest tort exposure but left SFUSD, opt-out PIs, and the antitrust action to be resolved on their own tracks.

**Remand Calculus**
At the December 16, 2025 case management conference, Orrick confirmed his intent to remand opt-out PIs after SJ. He asked plaintiff counsel a hard question: what supplemental discovery, after eight years of MDL, is genuinely non-duplicative? The January 6, 2026 joint letter narrowed it to deponent emails and social media records. He also flagged personal jurisdiction concerns over the NMDs in transferee state courts — federal RICO provides nationwide service of process; state-court analogs do not.

**What Comes Next**
The summary judgment ruling will be the next major event. Orrick takes time because his rulings are written to survive Ninth Circuit review on multiple grounds simultaneously. After SJ, expect a remand order, NMD personal jurisdiction analysis, and continued attention to the parallel antitrust trial September 28, 2026.

---

## 22. SETTLEMENT ANALYSIS

### 22.1 Settlement Ledger
| Date | Type | Side | Amount | Note |
|---|---|---|---|---|
| Dec 2022 | JLI Class Settlement (preliminary) | JLI | $1,700,000,000 | Master settlement ~10,000 PI claims |
| Jul 2023 | Altria Class Settlement (preliminary) | Altria | $235,000,000 | Altria-only fund |
| Sep 2023 | JLI Class Settlement (final approval) | Court | $1,700,000,000 | Judge Orrick approves |
| Sep 2022 | 34-State AG Settlement | JLI | $438,500,000 | Marketing restrictions |
| Mar 2024 | Altria Class Settlement (final) | Court | $235,000,000 | Bridge for opt-outs |
| Aug 2024 | Seliavski initial demand | Plaintiff | $250,000 | Pre-litigation (5x B.B. minor's compromise) |
| Feb 2025 | JLI opening opt-out offer | JLI | $35,000 | Aligned to documented past costs |
| Mar 2026 | Reduced Seliavski demand (post-MSJ posture) | Plaintiff | $175,000 | Acknowledges Sonner risk |

### 22.2 BATNA Analysis (Seliavski Cohort)
- **Plaintiff trial cost**: ~$8M (Two-track defense PI + antitrust = sustainable through consolidation)
- **Plaintiff prob-weighted outcome**: ~$12M
- **Plaintiff floor**: ~$4M (above class baseline + opt-out premium)
- **Defendant trial cost**: ~$25M (Two-front war premium)
- **Defendant ceiling**: ~$35M (compressed by parallel exposure)
- **Two-front war premium**: ~$12M (MDL PI remand trials + antitrust class trial Sept 28, 2026)
- **Settlement zone**: Exists for Seliavski-cohort opt-out PIs at consolidated treatment ($19.5M midpoint full cohort; $35K-$300K per plaintiff individually)

### 22.3 Comparable Cases
- **In re National Prescription Opiate Litigation MDL 2804** (N.D. Ohio Polster, 2022): $26B global settlement; same MDL coordinated structure, same public nuisance theory at municipal scale
- **City and County of San Francisco v. Purdue Pharma** (N.D. Cal. Breyer, 2022): $87.5M plaintiff verdict against Walgreens; identical court and identical municipal-plaintiff posture
- **B.B. minor's compromise (within JUUL MDL)** (N.D. Cal. Orrick Dkt.3716, 2024): $175,000; confidential minor's compromise approved
- **SFUSD-JLI prior settlement** (N.D. Cal. Orrick, 2022): $12,450,000 pre-verdict settlement

---

## 23. ANSWERING GUIDANCE

When this document is fed to a model:

1. **For factual questions** (dates, names, dollar amounts, docket numbers, filings): cite the specific section and reference number above. Do not infer or estimate.
2. **For procedural questions**: walk through Section 5 (Procedural Timeline) and Section 6 (Docket Entries).
3. **For substantive questions about theories**: use Sections 8 and 9.
4. **For caselaw questions**: use Section 12.
5. **For settlement questions**: use Section 22.
6. **For unknown facts not in this document**: respond *"I don't have that information in the case reference materials."* Do not guess. Do not extrapolate from general MDL knowledge.
7. **For dates referenced as "TBD"**: state the date is not yet set.
8. **For ongoing matters**: as of May 5, 2026, the MSJ ruling remains under submission (187 days); the antitrust opt-out deadline is June 13, 2026; the antitrust trial is September 28, 2026.

---

*End of Master Reference Document*
*Source: Compiled from 3:19-md-02913-WHO docket and case file as of May 2026*`;
